import type { RobotStatus } from '../api/types';
import { useLive } from '../live/store';
import { useAutonomy } from './autonomy';
import { useChargeView, type ChargeView } from './charge';
import { useHandPush } from './handPush';
import { useStuck, type StuckView } from './stuck';

/** The robot's current mode, one at a time (priority: estop > handPush > stuck > charging/going > autonomy > task > normal). */
export type RobotMode =
  | { kind: 'offline' }
  | { kind: 'estop' }
  | { kind: 'handPush' }
  | { kind: 'stuck'; failed: boolean }
  | { kind: 'charging' }
  | { kind: 'goingCharge' }
  | { kind: 'chargeFailed' }
  | { kind: 'autonomy'; state: string | null }
  | { kind: 'tour'; name: string | null; step: number | null; total: number | null }
  | { kind: 'navigation'; pointId: string | null }
  | { kind: 'task'; task: string }
  | { kind: 'normal' };

export function robotModeOf(
  online: boolean,
  st: RobotStatus | null | undefined,
  parts: { autonomyActive: boolean; autonomyState: string | null; stuck: StuckView | null; charge: ChargeView; handPushOn: boolean },
): RobotMode {
  if (!online) return { kind: 'offline' };
  if (st?.emergencyStop) return { kind: 'estop' };
  if (parts.handPushOn) return { kind: 'handPush' };
  if (parts.stuck) return { kind: 'stuck', failed: parts.stuck.kind === 'failed' };
  const task = st?.task ?? null;
  if (parts.charge === 'going' || task?.kind === 'dock') return { kind: 'goingCharge' };
  if (parts.charge === 'failed') return { kind: 'chargeFailed' };
  if (parts.charge === 'charging' || st?.charging) return { kind: 'charging' };
  if (parts.autonomyActive) return { kind: 'autonomy', state: parts.autonomyState };
  if (task?.kind === 'tour') return { kind: 'tour', name: task.name ?? null, step: task.step ?? null, total: task.total ?? null };
  if (task && (task.kind === 'navigate' || task.kind === 'goToPoint' || task.kind === 'goto' || task.kind === 'navigation')) return { kind: 'navigation', pointId: task.pointId ?? null };
  if (task?.kind) return { kind: 'task', task: task.kind };
  if (st?.navigating) return { kind: 'navigation', pointId: null };
  return { kind: 'normal' };
}

export const MODE_TONE: Record<RobotMode['kind'], 'grey' | 'blue' | 'orange' | 'green' | 'amber' | 'red'> = {
  offline: 'grey',
  normal: 'grey',
  estop: 'red',
  handPush: 'blue',
  stuck: 'amber',
  charging: 'green',
  goingCharge: 'amber',
  chargeFailed: 'red',
  autonomy: 'orange',
  tour: 'blue',
  navigation: 'blue',
  task: 'grey',
};

/** The robot's current mode (same priority as the chip). */
export function useRobotMode(serial: string): RobotMode {
  const online = useLive((s) => Boolean(s.robots[serial]?.online));
  const st = useLive((s) => s.robots[serial]?.status);
  const auto = useAutonomy(serial);
  const stuck = useStuck(serial);
  const charge = useChargeView(serial);
  const hand = useHandPush(serial);
  return robotModeOf(online, st, { autonomyActive: auto.active, autonomyState: auto.state, stuck, charge, handPushOn: hand.enabled });
}
